import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, Plus, Search, ChevronRight, Trash2, AlertTriangle, Calendar, TrendingUp, Pencil, Download, Briefcase, ShoppingBag } from "lucide-react";
import { toast, Toaster } from "sonner";
import CRMContactDialog from "../components/CRMContactDialog";
import { useAuth } from "../contexts/AuthContext";
import { stageLabel, STAGE_COLORS } from "../lib/crmStages";

const STAGES = ["all", "new", "contacted", "interested", "ordering", "inactive", "lost"];

export default function CRM({ basePath = "/admin/crm" }) {
    const { user } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [channel, setChannel] = useState("b2b");      // "b2b" | "ecommerce"
    const [stage, setStage] = useState("all");
    const [q, setQ] = useState("");
    const [followUp, setFollowUp] = useState("all");
    const [dlgOpen, setDlgOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importBusy, setImportBusy] = useState(false);

    const canManage = user?.role === "admin" || user?.role === "editor";

    const load = async () => {
        setLoading(true);
        try {
            const params = { channel };
            if (stage !== "all") params.stage = stage;
            if (q.trim()) params.q = q.trim();
            if (followUp !== "all") params.follow_up = followUp;
            const [c, d] = await Promise.all([
                api.get("/crm/contacts", { params }),
                api.get("/crm/dashboard", { params: { channel } }),
            ]);
            setContacts(c.data);
            setDashboard(d.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load CRM data");
            setContacts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel, stage, followUp]);
    useEffect(() => {
        const t = setTimeout(load, q.trim() ? 400 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const onCreate = () => { setEditing(null); setDlgOpen(true); };
    const onEdit = (c) => { setEditing(c); setDlgOpen(true); };

    const openImport = async () => {
        try {
            const { data } = await api.get("/crm/import-preview");
            setImportPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load import preview");
        }
    };

    const runImport = async () => {
        setImportBusy(true);
        try {
            const { data } = await api.post("/crm/import-from-customers");
            toast.success(`Imported ${data.imported} customer${data.imported === 1 ? "" : "s"} into CRM`);
            setImportPreview(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally {
            setImportBusy(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/crm/contacts/${deleteTarget.id}`);
            toast.success(`'${deleteTarget.name}' removed from CRM`);
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete contact");
        }
    };

    const kpis = useMemo(() => {
        if (!dashboard) return null;
        const bs = dashboard.by_stage || {};
        return {
            total: dashboard.total_contacts,
            pipeline_value: dashboard.pipeline_value,
            ordering: bs.ordering?.count || 0,
            stale: dashboard.stale_count,
            overdue: dashboard.overdue_count,
            today: dashboard.today_count,
        };
    }, [dashboard]);

    return (
        <div data-testid="crm-page">
            <Toaster position="top-center" />

            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Customer Relationship</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">CRM</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Manage every doctor, distributor, and prospect in one funnel.
                        Track calls, visits, follow-ups and watch pipeline grow.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canManage && (
                        <Button onClick={openImport} variant="outline" className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="crm-import">
                            <Download className="w-4 h-4 mr-2" />Import from Customer Master
                        </Button>
                    )}
                    <Button onClick={onCreate} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="crm-add">
                        <Plus className="w-4 h-4 mr-2" />New contact
                    </Button>
                </div>
            </div>

            {/* Channel tabs — split CRM into B2B (trade) and E-commerce (website retail) */}
            <div className="flex border border-stone-200 mb-4 bg-white" data-testid="crm-channel-tabs">
                {[
                    { key: "b2b", label: "B2B · Trade", sub: "Doctors, distributors, hospitals", Icon: Briefcase },
                    { key: "ecommerce", label: "E-commerce · Retail", sub: "Website customers", Icon: ShoppingBag },
                ].map(({ key, label, sub, Icon }) => {
                    const active = channel === key;
                    return (
                        <button
                            key={key}
                            onClick={() => { setChannel(key); setStage("all"); }}
                            className={`flex-1 px-4 py-3 text-left border-r last:border-r-0 border-stone-200 transition-colors ${active ? "bg-sky-700 text-white" : "hover:bg-sky-50"}`}
                            data-testid={`crm-channel-${key}`}
                        >
                            <div className="flex items-center gap-2 font-serif text-base">
                                <Icon className="w-4 h-4" />{label}
                            </div>
                            <div className={`text-[11px] mt-0.5 ${active ? "text-sky-100" : "text-stone-500"}`}>{sub}</div>
                        </button>
                    );
                })}
            </div>

            {/* KPI strip */}
            {kpis && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                    <KPI label="Total contacts" value={kpis.total} testid="kpi-total" />
                    <KPI label="Pipeline value" value={formatINR(kpis.pipeline_value)} cls="text-sky-700" testid="kpi-pipeline" />
                    <KPI label="Ordering now" value={kpis.ordering} cls="text-emerald-700" testid="kpi-ordering" />
                    <KPI label="Due today" value={kpis.today} cls="text-amber-700" icon={<Calendar className="w-3 h-3" />} testid="kpi-today" />
                    <KPI label="Overdue" value={kpis.overdue} cls="text-red-700" icon={<AlertTriangle className="w-3 h-3" />} testid="kpi-overdue" />
                    <KPI label="Stale leads" value={kpis.stale} cls="text-stone-700" icon={<TrendingUp className="w-3 h-3" />} testid="kpi-stale" />
                </div>
            )}

            {/* Pipeline funnel */}
            {dashboard?.by_stage && (
                <div className="bg-white border border-sky-100 mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-stone-100" data-testid="crm-funnel">
                    {["new", "contacted", "interested", "ordering", "inactive", "lost"].map((s) => {
                        const c = dashboard.by_stage[s] || { count: 0, value: 0 };
                        const isActive = stage === s;
                        return (
                            <button
                                key={s}
                                onClick={() => setStage(isActive ? "all" : s)}
                                className={`p-3 text-left transition-colors ${isActive ? "bg-sky-50" : "hover:bg-stone-50"}`}
                                data-testid={`funnel-${s}`}
                            >
                                <div className="text-[10px] uppercase tracking-widest text-stone-500">{stageLabel(channel, s)}</div>
                                <div className="font-serif text-2xl mt-1 text-slate-900">{c.count}</div>
                                {c.value > 0 && <div className="text-[11px] text-sky-700 font-mono mt-0.5">{formatINR(c.value)}</div>}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-sky-100 p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, email, city…" className="rounded-none border-stone-300 pl-9" data-testid="crm-search" />
                </div>
                <Select value={stage} onValueChange={setStage}>
                    <SelectTrigger className="rounded-none border-stone-300 w-44" data-testid="crm-stage-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All stages" : stageLabel(channel, s)}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={followUp} onValueChange={setFollowUp}>
                    <SelectTrigger className="rounded-none border-stone-300 w-44" data-testid="crm-followup-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All follow-ups</SelectItem>
                        <SelectItem value="today">Due today</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Contacts table */}
            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th>Name</Th>
                            <Th>Type</Th>
                            <Th>City / State</Th>
                            <Th>Stage</Th>
                            <Th>Pipeline</Th>
                            <Th>Last activity</Th>
                            <Th>Follow-up</Th>
                            <Th cls="text-right">Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : contacts.length === 0 ? (
                            <tr><td colSpan={8} className="p-12 text-center text-stone-500">No contacts yet — click "New contact" to add your first lead.</td></tr>
                        ) : contacts.map((c) => {
                            const fu = c.next_follow_up_at;
                            const today = new Date().toISOString().slice(0, 10);
                            const overdue = fu && fu < today;
                            const due = fu === today;
                            return (
                                <tr key={c.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`crm-row-${c.id}`}>
                                    <td className="p-3">
                                        <Link to={`${basePath}/${c.id}`} className="font-medium text-slate-900 hover:text-sky-700">
                                            {c.name}
                                        </Link>
                                        {c.company && <div className="text-[11px] text-stone-500">{c.company}</div>}
                                        {c.phone && <div className="text-[10px] text-stone-500 font-mono">{c.phone}</div>}
                                    </td>
                                    <td className="p-3 text-xs uppercase tracking-widest text-stone-500">{c.contact_type}</td>
                                    <td className="p-3 text-xs text-stone-600">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td>
                                    <td className="p-3">
                                        <Badge className={`rounded-none ${STAGE_COLORS[c.stage] || "bg-stone-100"}`}>{stageLabel(c.channel || channel, c.stage)}</Badge>
                                    </td>
                                    <td className="p-3 text-right font-mono text-sky-700">{c.estimated_value > 0 ? formatINR(c.estimated_value) : "—"}</td>
                                    <td className="p-3 text-xs text-stone-500">{c.last_activity_summary || "—"}</td>
                                    <td className={`p-3 text-xs ${overdue ? "text-red-700 font-semibold" : due ? "text-amber-700 font-semibold" : "text-stone-500"}`}>
                                        {fu ? fu : "—"}{overdue && " · overdue"}{due && " · today"}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <Link to={`${basePath}/${c.id}`} className="text-sky-700 hover:text-sky-800 inline-flex items-center text-xs px-2 py-1 border border-transparent hover:border-sky-200" data-testid={`crm-view-${c.id}`}>
                                                View<ChevronRight className="w-3 h-3 ml-0.5" />
                                            </Link>
                                            <button onClick={() => onEdit(c)} className="text-amber-700 hover:bg-amber-50 border border-amber-300 p-1 inline-flex items-center" data-testid={`crm-edit-${c.id}`} title="Edit contact">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            {canManage && (
                                                <button onClick={() => setDeleteTarget(c)} className="text-red-700 hover:bg-red-50 border border-red-300 p-1 inline-flex items-center" data-testid={`crm-delete-${c.id}`} title="Delete contact">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Leaderboard (admin/editor only) */}
            {dashboard?.leaderboard?.length > 0 && (
                <div className="mt-6 bg-white border border-sky-100" data-testid="crm-leaderboard">
                    <div className="p-3 border-b border-sky-100 bg-sky-50/40">
                        <div className="label-eyebrow text-sky-700">MR Leaderboard</div>
                        <div className="font-serif text-lg text-slate-900">Top sales reps by pipeline</div>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-100">
                                <Th>#</Th><Th>MR</Th><Th>Territory</Th>
                                <Th cls="text-right">Contacts</Th>
                                <Th cls="text-right">Active pipeline</Th>
                                <Th cls="text-right">Ordering</Th>
                                <Th cls="text-right">Pipeline value</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {dashboard.leaderboard.map((m, i) => (
                                <tr key={m.mr_id} className="border-b border-stone-100">
                                    <td className="p-3 font-mono text-stone-400">{i + 1}</td>
                                    <td className="p-3 font-medium">{m.mr_name}<div className="text-[10px] text-stone-500">{m.mr_email}</div></td>
                                    <td className="p-3 text-xs text-stone-600">{m.territory || "—"}</td>
                                    <td className="p-3 text-right font-mono">{m.contacts}</td>
                                    <td className="p-3 text-right font-mono text-sky-700">{m.active_pipeline}</td>
                                    <td className="p-3 text-right font-mono text-emerald-700 font-semibold">{m.ordering}</td>
                                    <td className="p-3 text-right font-mono">{formatINR(m.estimated_value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Contact create/edit dialog */}
            <CRMContactDialog
                open={dlgOpen}
                onOpenChange={setDlgOpen}
                contact={editing}
                defaultChannel={channel}
                onSaved={() => load()}
            />

            {/* Import preview & confirm */}
            <AlertDialog open={!!importPreview} onOpenChange={(o) => !o && setImportPreview(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Import from Customer Master?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {importPreview?.total_customers === 0 ? (
                                <>Your Customer Master is empty — nothing to import.</>
                            ) : importPreview?.will_import === 0 ? (
                                <>All <strong>{importPreview?.total_customers}</strong> customers are already in the CRM. Nothing new to import.</>
                            ) : (
                                <>
                                    <strong className="text-sky-700">{importPreview?.will_import}</strong> new customer(s) will be added to the CRM in the <strong>new</strong> stage,
                                    tagged with <span className="font-mono bg-amber-100 px-1.5 py-0.5">imported</span> for filtering.
                                    {importPreview?.already_in_crm > 0 && (
                                        <span className="block mt-2 text-xs text-stone-500">
                                            {importPreview.already_in_crm} customer(s) already in CRM will be skipped (re-running this import is safe).
                                        </span>
                                    )}
                                </>
                            )}
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="crm-import-cancel">Cancel</AlertDialogCancel>
                        {importPreview?.will_import > 0 && (
                            <AlertDialogAction onClick={runImport} disabled={importBusy} className="bg-amber-600 hover:bg-amber-700 rounded-none" data-testid="crm-import-confirm">
                                {importBusy ? "Importing…" : `Import ${importPreview?.will_import} contact${importPreview?.will_import === 1 ? "" : "s"}`}
                            </AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete '{deleteTarget?.name}'?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the contact from your CRM and clears its timeline.
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="crm-delete-confirm">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const Th = ({ children, cls = "" }) => (
    <th className={`p-2 text-[10px] uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>
);
const KPI = ({ label, value, sub, cls = "", icon, testid }) => (
    <div className="bg-white border border-sky-100 p-3" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">{icon}{label}</div>
        <div className={`font-serif text-2xl mt-1 ${cls}`}>{value}</div>
        {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
);
